import { useColorMode } from "@kobalte/core/color-mode";
import { createFileRoute, redirect } from "@tanstack/solid-router";
import { createServerFn } from "@tanstack/solid-start";
import type { ChartData, ChartOptions } from "chart.js";
import { createMemo, createSignal, For, Show, type JSX } from "solid-js";
import {
  IconBuilding2,
  IconCalculator,
  IconCpu,
  IconHardDrive,
  IconLandmark,
  IconShieldAlert,
  IconWrench,
} from "~/components/icons";
import { StandardRouteDetailHeader } from "~/components/standard-route/detail/header";
import { Badge } from "~/components/ui/badge";
import { Callout, CalloutContent, CalloutTitle } from "~/components/ui/callout";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { BarChart } from "~/components/ui/charts";
import {
  Slider,
  SliderFill,
  SliderThumb,
  SliderTrack,
} from "~/components/ui/slider";
import { cn } from "~/lib/utils";
import { isAdminUser } from "~/server/admin";
import { getAiBillingConfig } from "~/server/ai-billing";
import { authFunctionMiddleware } from "~/server/middleware/auth";
import { PLANS } from "~/server/plans";

// ---------------------------------------------------------------------------
// Admin gating — the user table has a `role` column ('user' | 'admin')
// maintained directly in the DB (e.g. UPDATE "user" SET role='admin'
// WHERE email=...). The check happens server-side inside the server fn; the
// route loader redirects non-admins back to the dashboard. ADMIN_EMAILS
// (comma-separated env) remains as a bootstrap fallback only.
// ---------------------------------------------------------------------------

function isAdminEmail(email: string | null | undefined): boolean {
  const allow = (process.env.ADMIN_EMAILS ?? "")
    .split(",")
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean);
  return !!email && allow.includes(email.toLowerCase());
}

const getPricingSimulatorConfig = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .handler(async ({ context }) => {
    if (
      !(await isAdminUser(context.user.id)) &&
      !isAdminEmail(context.user.email)
    ) {
      throw Response.json(
        { error: "Forbidden", code: "ADMIN_ONLY" },
        { status: 403 },
      );
    }
    const aiConfig = getAiBillingConfig();
    return {
      plans: PLANS.map((p) => ({
        tier: p.tier,
        name: p.name,
        priceUsd: p.priceUsd,
        includedAiCredits: p.includedAiCredits,
        includedStorageGb: p.includedStorageGb,
        businessLimit: p.businessLimit,
        includedBankConnections: p.includedBankConnections,
        creditPriceUsd: p.creditPriceUsd,
        storagePriceMbUsd: p.storagePriceMbUsd,
        extraConnectionPriceUsd: p.extraConnectionPriceUsd,
      })),
      aiCreditValue: aiConfig.creditValue,
      aiMarkupPercent: aiConfig.markupPercent,
    };
  });

export const Route = createFileRoute(
  "/_dashboard/dashboard/admin/pricing-simulator",
)({
  staticData: { breadcrumb: "Pricing Simulator" },
  loader: async () => {
    // Thrown Responses RESOLVE client-side with { error } — shape-check.
    let result: Awaited<ReturnType<typeof getPricingSimulatorConfig>> | null =
      null;
    try {
      result = await getPricingSimulatorConfig();
    } catch {
      result = null;
    }
    if (
      !result ||
      !Array.isArray(result.plans) ||
      typeof result.aiCreditValue !== "number"
    ) {
      throw redirect({ to: "/dashboard" });
    }
    return result;
  },
  component: PricingSimulatorPage,
});

type Tier = "free" | "basic" | "pro" | "business";

const fmtMoney = (n: number, digits = 2) =>
  n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

const fmtPct = (n: number | null) => (n === null ? "—" : `${n.toFixed(1)}%`);

function NumberInput(props: {
  value: number;
  onChange: (v: number) => void;
  step?: number | "any";
  min?: number;
  max?: number;
  class?: string;
}): JSX.Element {
  return (
    <input
      type="number"
      step={props.step ?? "any"}
      min={props.min}
      max={props.max}
      value={props.value}
      onInput={(e) => {
        const v = parseFloat(e.currentTarget.value);
        props.onChange(Number.isFinite(v) ? v : 0);
      }}
      class={cn(
        "h-8 w-24 rounded-md border bg-background px-2 text-right text-sm tabular-nums focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        props.class,
      )}
    />
  );
}

function SimSlider(props: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step?: number;
  format?: (v: number) => string;
}): JSX.Element {
  return (
    <div class="space-y-1.5">
      <div class="flex items-center justify-between gap-2">
        <span class="text-xs text-muted-foreground">{props.label}</span>
        <span class="text-xs font-medium tabular-nums">
          {props.format ? props.format(props.value) : props.value}
        </span>
      </div>
      <Slider
        value={[props.value]}
        onChange={(v) => props.onChange(v[0] ?? props.value)}
        minValue={props.min}
        maxValue={props.max}
        step={props.step ?? 1}
        class="w-full"
      >
        <SliderTrack>
          <SliderFill />
          <SliderThumb />
        </SliderTrack>
      </Slider>
    </div>
  );
}

function SectionCard(props: {
  title: string;
  icon: JSX.Element;
  children: JSX.Element;
  class?: string;
}): JSX.Element {
  return (
    <Card class={cn("overflow-hidden", props.class)}>
      <CardHeader class="pb-3">
        <CardTitle class="flex items-center gap-2 text-sm font-semibold">
          {props.icon}
          {props.title}
        </CardTitle>
      </CardHeader>
      <CardContent class="space-y-4">{props.children}</CardContent>
    </Card>
  );
}

function PricingSimulatorPage(): JSX.Element {
  const config = Route.useLoaderData();
  const { colorMode } = useColorMode();

  // --- COGS assumptions (editable) ---
  const [iDriveCost, setIDriveCost] = createSignal(49.5); // $/TB/yr
  const [backupMultiplier, setBackupMultiplier] = createSignal(2);
  const [aiMarkup, setAiMarkup] = createSignal(config().aiMarkupPercent); // %
  const [aiTaxPct, setAiTaxPct] = createSignal(5); // % pass-through
  const [plaidCost, setPlaidCost] = createSignal(0.3); // $/connection/mo
  const [railwayCost, setRailwayCost] = createSignal(15); // $/mo
  const [stripePct, setStripePct] = createSignal(2.9); // %
  const [stripeFixed, setStripeFixed] = createSignal(0.3); // $

  // --- Usage utilization (editable) ---
  const [aiUtil, setAiUtil] = createSignal(30); // %
  const [storageUtil, setStorageUtil] = createSignal(40); // %
  const [bankUtil, setBankUtil] = createSignal(80); // %

  // --- Editable plan prices ---
  const initialPrices = (() => {
    const init: Record<Tier, number> = {
      free: 0,
      basic: 0,
      pro: 0,
      business: 0,
    };
    for (const p of config().plans) {
      init[p.tier as Tier] = p.priceUsd;
    }
    return init;
  })();
  const [prices, setPrices] = createSignal<Record<Tier, number>>(initialPrices);

  // --- Editable included entitlements per plan ---
  type IncludedEntitlements = Record<
    Tier,
    { ai: number; storage: number; banks: number; businessLimit: number }
  >;
  const initialIncluded = (() => {
    const init: IncludedEntitlements = {
      free: { ai: 0, storage: 0, banks: 0, businessLimit: 1 },
      basic: { ai: 0, storage: 0, banks: 0, businessLimit: 1 },
      pro: { ai: 0, storage: 0, banks: 0, businessLimit: 3 },
      business: { ai: 0, storage: 0, banks: 0, businessLimit: 10 },
    };
    for (const p of config().plans) {
      init[p.tier as Tier] = {
        ai: p.includedAiCredits,
        storage: p.includedStorageGb,
        banks: p.includedBankConnections,
        businessLimit: p.businessLimit ?? 1,
      };
    }
    return init;
  })();
  const [included, setIncluded] =
    createSignal<IncludedEntitlements>(initialIncluded);

  const updateIncluded = (
    tier: Tier,
    key: keyof IncludedEntitlements[Tier],
    v: number,
  ) =>
    setIncluded((prev) => ({
      ...prev,
      [tier]: { ...prev[tier], [key]: v },
    }));

  // --- Editable users per plan (drive the hosting split) ---
  const [usersPerPlan, setUsersPerPlan] = createSignal<Record<Tier, number>>({
    free: 1000,
    basic: 200,
    pro: 50,
    business: 10,
  });

  // --- Editable overage rates ---
  const [overage, setOverage] = createSignal({
    ai: 0.0001,
    storage: 0.05,
    bank: 2.0,
    business: 2.0, // $/extra business/mo
  });

  // --- Derived unit economics ---
  const aiCogsPerCredit = createMemo(
    () =>
      (config().aiCreditValue / (1 + aiMarkup() / 100)) *
      (1 + aiTaxPct() / 100),
  );
  const storageCogsPerGB = createMemo(
    () => (iDriveCost() * backupMultiplier()) / 1000 / 12,
  );

  // Every user (free included) shares the Railway hosting bill evenly.
  const totalUsers = createMemo(() =>
    (["free", "basic", "pro", "business"] as Tier[]).reduce(
      (sum, t) => sum + (usersPerPlan()[t] ?? 0),
      0,
    ),
  );
  const hostingPerUser = createMemo(() =>
    totalUsers() > 0 ? railwayCost() / totalUsers() : 0,
  );

  const rows = createMemo(() =>
    config().plans.map((p) => {
      const price = prices()[p.tier as Tier] ?? p.priceUsd;
      const ent = included()[p.tier as Tier] ?? initialIncluded[p.tier as Tier];
      const aiCogs = ent.ai * (aiUtil() / 100) * aiCogsPerCredit();
      const storageCogs =
        ent.storage * (storageUtil() / 100) * storageCogsPerGB();
      const bankCogs = ent.banks * (bankUtil() / 100) * plaidCost();
      const hostingAlloc = hostingPerUser();
      const stripeFee =
        price > 0 ? price * (stripePct() / 100) + stripeFixed() : 0;
      const total = aiCogs + storageCogs + bankCogs + hostingAlloc + stripeFee;
      const margin = price - total;
      const marginPct = price > 0 ? (margin / price) * 100 : null;
      return {
        ...p,
        price,
        ent,
        aiCogs,
        storageCogs,
        bankCogs,
        hostingAlloc,
        stripeFee,
        total,
        margin,
        marginPct,
      };
    }),
  );

  const overageMargins = createMemo(() => ({
    ai: overage().ai - aiCogsPerCredit(),
    storage: overage().storage - storageCogsPerGB(),
    bank: overage().bank - plaidCost(),
    business: overage().business,
  }));

  const freeLoss = () => rows().find((r) => r.tier === "free")?.total ?? 0;

  const chartData = createMemo<ChartData>(() => ({
    labels: rows().map((r) => r.name),
    datasets: [
      {
        label: "Revenue",
        data: rows().map((r) => Number(r.price.toFixed(2))),
        backgroundColor: "rgba(62,207,174,0.85)",
        borderRadius: 4,
        borderSkipped: false,
        // Free tier has $0 revenue → no bar (chart.js ignores minBarLength for 0 values)
        minBarLength: 4,
      },
      {
        label: "COGS",
        data: rows().map((r) => Number(r.total.toFixed(2))),
        backgroundColor: "rgba(240,122,106,0.85)",
        borderRadius: 4,
        borderSkipped: false,
        // Free tier costs $0.03/user/mo — tiny vs the ~$30 axis, so force a visible sliver
        minBarLength: 4,
      },
    ],
  }));

  // Total monthly profit/loss for each plan's whole user base:
  // (price − COGS) × users on that plan. Separate chart since it's
  // orders of magnitude larger than per-user numbers.
  const profitChartData = createMemo<ChartData>(() => ({
    labels: rows().map((r) => r.name),
    datasets: [
      {
        label: "Net profit (total)",
        data: rows().map((r) =>
          Number(
            ((r.price - r.total) * (usersPerPlan()[r.tier] ?? 0)).toFixed(0),
          ),
        ),
        backgroundColor: rows().map((r) =>
          (r.price - r.total) * (usersPerPlan()[r.tier] ?? 0) < 0
            ? "rgba(240,122,106,0.85)"
            : "rgba(52,211,153,0.85)",
        ),
        borderRadius: 4,
        borderSkipped: false,
      },
    ],
  }));

  const chartOptions = createMemo<ChartOptions>(() => {
    const dark = colorMode() === "dark";
    const tickColor = dark ? "hsl(240, 5%, 64.9%)" : "hsl(240, 3.8%, 30%)";
    const gridColor = dark
      ? "rgba(255,255,255,0.07)"
      : "rgba(128,128,128,0.08)";
    return {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          border: { display: false },
          grid: { display: false },
          ticks: {
            color: tickColor,
            font: { size: 11, family: "'DM Mono', monospace" } as any,
            maxRotation: 0,
          },
        },
        y: {
          display: true,
          border: { display: false },
          grid: { color: gridColor },
          ticks: {
            maxTicksLimit: 5,
            color: tickColor,
            font: { size: 11, family: "'DM Mono', monospace" } as any,
            callback: (v: number | string) => `$${+v}`,
          },
        },
      },
      plugins: {
        legend: {
          display: true,
          position: "bottom" as const,
          labels: {
            usePointStyle: true,
            pointStyle: "circle" as const,
            boxWidth: 6,
            boxHeight: 6,
            color: tickColor,
            font: { size: 12, family: "'DM Mono', monospace" } as any,
            padding: 16,
          },
        },
        tooltip: {
          enabled: true,
          backgroundColor: dark
            ? "rgba(50,50,60,0.95)"
            : "rgba(255,255,255,0.96)",
          titleColor: dark ? "#fff" : "hsl(240, 10%, 10%)",
          bodyColor: dark ? "#fff" : "hsl(240, 10%, 10%)",
          borderColor: dark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)",
          borderWidth: 1,
          padding: 8,
          cornerRadius: 6,
          callbacks: {
            label: (ctx) =>
              ` ${ctx.dataset.label}: $${(ctx.parsed.y ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
          },
        },
      },
    };
  });

  return (
    <div class="flex flex-1 flex-col gap-6 px-4 pb-3 pt-10 sm:px-6">
      <StandardRouteDetailHeader
        icon={<IconCalculator class="size-5" />}
        title="Pricing Simulator"
        badge="Admin"
      />

      <Callout variant="warning">
        <CalloutTitle class="text-sm">
          <IconShieldAlert class="mr-1 inline size-4 align-text-bottom" />
          Admin-only · internal COGS tool
        </CalloutTitle>
        <CalloutContent class="text-xs">
          Values are editable assumptions for planning — nothing here changes
          live billing. Plan entitlements are read from the current plan
          catalog; adjust the sliders to model different COGS.
        </CalloutContent>
      </Callout>

      <div class="grid grid-cols-1 gap-6 xl:grid-cols-2">
        {/* COGS assumptions */}
        <SectionCard
          title="COGS Assumptions"
          icon={<IconWrench class="size-4 text-muted-foreground" />}
        >
          <SimSlider
            label="iDrive storage cost"
            value={iDriveCost()}
            onChange={setIDriveCost}
            min={0}
            max={500}
            step={0.5}
            format={(v) => `$${v.toFixed(2)}/TB/yr`}
          />
          <SimSlider
            label="Backup multiplier"
            value={backupMultiplier()}
            onChange={setBackupMultiplier}
            min={1}
            max={10}
            step={0.5}
            format={(v) => `${v.toFixed(1)}×`}
          />
          <SimSlider
            label="AI markup"
            value={aiMarkup()}
            onChange={setAiMarkup}
            min={0}
            max={200}
            step={1}
            format={(v) => `${v.toFixed(0)}%`}
          />
          <SimSlider
            label="AI tax pass-through"
            value={aiTaxPct()}
            onChange={setAiTaxPct}
            min={0}
            max={50}
            step={0.5}
            format={(v) => `${v.toFixed(1)}%`}
          />
          <SimSlider
            label="Plaid cost"
            value={plaidCost()}
            onChange={setPlaidCost}
            min={0}
            max={5}
            step={0.05}
            format={(v) => `$${v.toFixed(2)}/conn/mo`}
          />
          <SimSlider
            label="Railway hosting"
            value={railwayCost()}
            onChange={setRailwayCost}
            min={0}
            max={500}
            step={1}
            format={(v) => `$${v.toFixed(0)}/mo`}
          />
          <SimSlider
            label="Stripe fee"
            value={stripePct()}
            onChange={setStripePct}
            min={0}
            max={10}
            step={0.1}
            format={(v) => `${v.toFixed(1)}% + $${stripeFixed().toFixed(2)}`}
          />
        </SectionCard>

        {/* Utilization + pricing */}
        <div class="flex flex-col gap-6">
          <SectionCard
            title="Usage Utilization"
            icon={<IconCpu class="size-4 text-muted-foreground" />}
          >
            <SimSlider
              label="AI credits utilized"
              value={aiUtil()}
              onChange={setAiUtil}
              min={0}
              max={100}
              step={1}
              format={(v) => `${v}%`}
            />
            <SimSlider
              label="Storage utilized"
              value={storageUtil()}
              onChange={setStorageUtil}
              min={0}
              max={100}
              step={1}
              format={(v) => `${v}%`}
            />
            <SimSlider
              label="Bank connections utilized"
              value={bankUtil()}
              onChange={setBankUtil}
              min={0}
              max={100}
              step={1}
              format={(v) => `${v}%`}
            />
          </SectionCard>

          <SectionCard
            title="Plan Pricing & Entitlements"
            icon={<IconLandmark class="size-4 text-muted-foreground" />}
          >
            <div class="grid grid-cols-[minmax(0,1fr)_repeat(6,minmax(4.25rem,1fr))] items-center gap-x-2 gap-y-1.5 text-[11px] text-muted-foreground/70">
              <span>Plan</span>
              <span class="text-right">Price $</span>
              <span class="text-right">Users</span>
              <span class="text-right">AI credits</span>
              <span class="text-right">Storage GB</span>
              <span class="text-right">Banks</span>
              <span class="text-right">Biz limit</span>
              <For each={config().plans}>
                {(p) => {
                  const tier = p.tier as Tier;
                  return (
                    <>
                      <span class="truncate text-xs text-muted-foreground">
                        {p.name}
                      </span>
                      <NumberInput
                        value={prices()[tier] ?? p.priceUsd}
                        onChange={(v) =>
                          setPrices((prev) => ({ ...prev, [tier]: v }))
                        }
                        step={1}
                        min={0}
                        class="w-full"
                      />
                      <NumberInput
                        value={usersPerPlan()[tier] ?? 0}
                        onChange={(v) =>
                          setUsersPerPlan((prev) => ({ ...prev, [tier]: v }))
                        }
                        step={1}
                        min={0}
                        class="w-full"
                      />
                      <NumberInput
                        value={included()[tier]?.ai ?? p.includedAiCredits}
                        onChange={(v) => updateIncluded(tier, "ai", v)}
                        step={1000}
                        min={0}
                        class="w-full"
                      />
                      <NumberInput
                        value={included()[tier]?.storage ?? p.includedStorageGb}
                        onChange={(v) => updateIncluded(tier, "storage", v)}
                        step={1}
                        min={0}
                        class="w-full"
                      />
                      <NumberInput
                        value={
                          included()[tier]?.banks ?? p.includedBankConnections
                        }
                        onChange={(v) => updateIncluded(tier, "banks", v)}
                        step={1}
                        min={0}
                        class="w-full"
                      />
                      <NumberInput
                        value={
                          included()[tier]?.businessLimit ??
                          p.businessLimit ??
                          1
                        }
                        onChange={(v) =>
                          updateIncluded(tier, "businessLimit", v)
                        }
                        step={1}
                        min={0}
                        class="w-full"
                      />
                    </>
                  );
                }}
              </For>
            </div>
            <p class="text-[11px] text-muted-foreground/70">
              Included entitlements start from the plan catalog — edit to model
              changes. Nothing here changes live billing. Users per plan split
              the Railway hosting bill evenly across all users (free included).
            </p>
          </SectionCard>

          <SectionCard
            title="Overage Rates"
            icon={<IconBuilding2 class="size-4 text-muted-foreground" />}
          >
            <div class="flex items-center justify-between gap-3">
              <span class="text-xs text-muted-foreground">
                AI credits{" "}
                <span class="text-[11px] text-muted-foreground/60">
                  (per credit)
                </span>
              </span>
              <NumberInput
                value={overage().ai}
                onChange={(v) => setOverage((prev) => ({ ...prev, ai: v }))}
                step={0.0001}
                min={0}
              />
            </div>
            <div class="flex items-center justify-between gap-3">
              <span class="text-xs text-muted-foreground">
                Storage{" "}
                <span class="text-[11px] text-muted-foreground/60">
                  (per GB/mo)
                </span>
              </span>
              <NumberInput
                value={overage().storage}
                onChange={(v) =>
                  setOverage((prev) => ({ ...prev, storage: v }))
                }
                step={0.05}
                min={0}
              />
            </div>
            <div class="flex items-center justify-between gap-3">
              <span class="text-xs text-muted-foreground">
                Bank connections{" "}
                <span class="text-[11px] text-muted-foreground/60">
                  (per conn/mo)
                </span>
              </span>
              <NumberInput
                value={overage().bank}
                onChange={(v) => setOverage((prev) => ({ ...prev, bank: v }))}
                step={0.5}
                min={0}
              />
            </div>
            <div class="flex items-center justify-between gap-3">
              <span class="text-xs text-muted-foreground">Businesses</span>
              <NumberInput
                value={overage().business}
                onChange={(v) =>
                  setOverage((prev) => ({ ...prev, business: v }))
                }
                step={1}
                min={0}
              />
            </div>
          </SectionCard>
        </div>
      </div>

      {/* Margin-by-tier table */}
      <Card class="overflow-hidden">
        <CardHeader class="pb-3">
          <CardTitle class="flex items-center gap-2 text-sm font-semibold">
            <IconCalculator class="size-4 text-muted-foreground" />
            Margin by Tier
          </CardTitle>
        </CardHeader>
        <CardContent class="overflow-x-auto p-0">
          <table class="w-full min-w-225 text-sm">
            <thead>
              <tr class="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <th class="px-4 py-2 font-medium">Tier</th>
                <th class="px-4 py-2 text-right font-medium">Price</th>
                <th class="px-4 py-2 text-right font-medium">AI COGS</th>
                <th class="px-4 py-2 text-right font-medium">Storage</th>
                <th class="px-4 py-2 text-right font-medium">Plaid</th>
                <th class="px-4 py-2 text-right font-medium">Hosting</th>
                <th class="px-4 py-2 text-right font-medium">Stripe</th>
                <th class="px-4 py-2 text-right font-medium">Total COGS</th>
                <th class="px-4 py-2 text-right font-medium">Margin $</th>
                <th class="px-4 py-2 text-right font-medium">Margin %</th>
              </tr>
            </thead>
            <tbody>
              <For each={rows()}>
                {(r) => (
                  <tr class="border-b border-border/60 last:border-b-0">
                    <td class="px-4 py-2.5 font-medium">{r.name}</td>
                    <td class="px-4 py-2.5 text-right tabular-nums">
                      {fmtMoney(r.price)}
                    </td>
                    <td class="px-4 py-2.5 text-right tabular-nums">
                      {fmtMoney(r.aiCogs)}
                    </td>
                    <td class="px-4 py-2.5 text-right tabular-nums">
                      {fmtMoney(r.storageCogs)}
                    </td>
                    <td class="px-4 py-2.5 text-right tabular-nums">
                      {fmtMoney(r.bankCogs)}
                    </td>
                    <td class="px-4 py-2.5 text-right tabular-nums">
                      {fmtMoney(r.hostingAlloc)}
                    </td>
                    <td class="px-4 py-2.5 text-right tabular-nums">
                      {fmtMoney(r.stripeFee)}
                    </td>
                    <td class="px-4 py-2.5 text-right font-medium tabular-nums">
                      {fmtMoney(r.total)}
                    </td>
                    <td
                      class={cn(
                        "px-4 py-2.5 text-right font-medium tabular-nums",
                        r.margin < 0
                          ? "text-error-foreground"
                          : "text-emerald-600 dark:text-emerald-400",
                      )}
                    >
                      {fmtMoney(r.margin)}
                    </td>
                    <td
                      class={cn(
                        "px-4 py-2.5 text-right font-medium tabular-nums",
                        (r.marginPct ?? 0) < 0
                          ? "text-error-foreground"
                          : "text-emerald-600 dark:text-emerald-400",
                      )}
                    >
                      {fmtPct(r.marginPct)}
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Revenue/COGS + total profit charts */}
      <div class="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Card class="overflow-hidden">
          <CardHeader class="pb-3">
            <CardTitle class="flex items-center gap-2 text-sm font-semibold">
              <IconHardDrive class="size-4 text-muted-foreground" />
              Revenue vs COGS
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div class="h-64">
              <BarChart data={chartData()} options={chartOptions()} />
            </div>
          </CardContent>
        </Card>

        <Card class="overflow-hidden">
          <CardHeader class="pb-3">
            <CardTitle class="flex items-center gap-2 text-sm font-semibold">
              <IconLandmark class="size-4 text-muted-foreground" />
              Net Profit by Plan
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div class="h-64">
              <BarChart data={profitChartData()} options={chartOptions()} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Overage margin per unit */}
      <div>
        <h3 class="mb-3 text-sm font-semibold">Overage Margin Per Unit</h3>
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <OverageCard
            label="AI credit"
            icon={<IconCpu class="size-4" />}
            revenue={overage().ai}
            cogs={aiCogsPerCredit()}
            margin={overageMargins().ai}
            revenueFormat={(v) => `$${v.toFixed(6)}/credit`}
            cogsFormat={(v) => `$${v.toFixed(6)}/credit`}
          />
          <OverageCard
            label="Storage"
            icon={<IconHardDrive class="size-4" />}
            revenue={overage().storage}
            cogs={storageCogsPerGB()}
            margin={overageMargins().storage}
            revenueFormat={(v) => `$${v.toFixed(3)}/GB/mo`}
            cogsFormat={(v) => `$${v.toFixed(4)}/GB/mo`}
          />
          <OverageCard
            label="Bank connection"
            icon={<IconLandmark class="size-4" />}
            revenue={overage().bank}
            cogs={plaidCost()}
            margin={overageMargins().bank}
            revenueFormat={(v) => `$${v.toFixed(2)}/conn/mo`}
            cogsFormat={(v) => `$${v.toFixed(2)}/conn/mo`}
          />
          <OverageCard
            label="Extra business"
            icon={<IconBuilding2 class="size-4" />}
            revenue={overage().business}
            cogs={0}
            margin={overageMargins().business}
            revenueFormat={(v) => `$${v.toFixed(2)}/biz/mo`}
            cogsFormat={() => "—"}
          />
        </div>
      </div>

      {/* Free-tier loss-leader callout */}
      <Show when={freeLoss() > 0}>
        <Callout variant="default">
          <CalloutTitle class="text-sm">
            <IconShieldAlert class="mr-1 inline size-4 align-text-bottom" />
            Free tier is a loss leader
          </CalloutTitle>
          <CalloutContent class="text-xs">
            At current assumptions the free tier costs {fmtMoney(freeLoss())}
            /mo per user in COGS (AI, storage, hosting) with zero revenue —
            priced to convert, not to profit.
          </CalloutContent>
        </Callout>
      </Show>
    </div>
  );
}

function OverageCard(props: {
  label: string;
  icon: JSX.Element;
  revenue: number;
  cogs: number;
  margin: number;
  revenueFormat: (v: number) => string;
  cogsFormat: (v: number) => string;
  note?: string;
}): JSX.Element {
  const positive = () => props.margin > 0;
  return (
    <Card class="overflow-hidden">
      <CardContent class="space-y-2 p-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2 text-sm font-medium">
            {props.icon}
            {props.label}
          </div>
          <Show when={props.note}>
            <Badge variant="outline" class="text-[10px]">
              {props.note}
            </Badge>
          </Show>
        </div>
        <div class="space-y-1 text-xs">
          <div class="flex items-center justify-between">
            <span class="text-muted-foreground">Overage price</span>
            <span class="tabular-nums">
              {props.revenueFormat(props.revenue)}
            </span>
          </div>
          <div class="flex items-center justify-between">
            <span class="text-muted-foreground">COGS</span>
            <span class="tabular-nums">{props.cogsFormat(props.cogs)}</span>
          </div>
        </div>
        <div
          class={cn(
            "flex items-center justify-between rounded-md px-2 py-1.5 text-sm font-semibold tabular-nums",
            positive()
              ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
              : "bg-error text-error-foreground",
          )}
        >
          <span>Margin</span>
          <span>{fmtMoney(props.margin)}</span>
        </div>
      </CardContent>
    </Card>
  );
}
